import { useEffect, useRef, useState } from 'react';

import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import { useTranslation } from 'react-i18next';

import type { AccentName } from '@/theme';
import { motion } from '@/theme';

interface VoteButtonProps {
  count: number;
  hasUpvoted: boolean;
  onToggle: () => void;
  /** Disabled with an explanation — signed out, or email not confirmed. */
  disabledReason?: string;
  accent?: AccentName;
  size?: 'small' | 'medium';
}

/**
 * The upvote control (spec §9.2): fills with the register's colour when active,
 * with a small spring on press.
 *
 * The count lives in an `aria-live` region so a screen-reader user hears it
 * change, and the button's own accessible name says what pressing it will *do*
 * rather than describing the arrow.
 */
export function VoteButton({
  count,
  hasUpvoted,
  onToggle,
  disabledReason,
  accent = 'good',
  size = 'small',
}: VoteButtonProps) {
  const { t } = useTranslation();
  const [pressed, setPressed] = useState(false);
  const previousCount = useRef(count);

  useEffect(() => {
    if (count !== previousCount.current) {
      previousCount.current = count;
      setPressed(true);
      const timer = setTimeout(() => setPressed(false), motion.base);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [count]);

  const upvotes = t('vote.count', { count });
  const label = disabledReason
    ? upvotes
    : hasUpvoted
      ? t('vote.remove', { upvotes })
      : t('vote.add', { upvotes });

  const button = (
    <Button
      size={size}
      variant="text"
      onClick={onToggle}
      disabled={Boolean(disabledReason)}
      aria-pressed={hasUpvoted}
      aria-label={label}
      startIcon={
        <ArrowUpwardRoundedIcon
          fontSize="small"
          sx={{
            transform: pressed ? 'translateY(-2px) scale(1.15)' : 'none',
            transition: `transform ${motion.base}ms ${motion.spring}`,
          }}
        />
      }
      sx={{
        color: hasUpvoted ? `${accent}.text` : 'text.secondary',
        backgroundColor: hasUpvoted ? `${accent}.soft` : 'transparent',
        '&:hover': {
          backgroundColor: hasUpvoted ? `${accent}.soft` : 'action.hover',
        },
      }}
    >
      {/* Announced politely, so the new total is heard without stealing focus. */}
      <Box component="span" aria-live="polite" aria-atomic="true">
        {count}
      </Box>
    </Button>
  );

  if (!disabledReason) return button;

  return (
    <Tooltip title={disabledReason}>
      {/* A disabled button fires no pointer events, so the tooltip needs a wrapper. */}
      <span>{button}</span>
    </Tooltip>
  );
}
